import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { XMarkIcon, PhotoIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const TestimonialModal = ({ testimonial, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        rating: 5,
        text: '',
        loan_type: '',
        display_order: 0,
        is_active: true
    });
    const [image, setImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (testimonial) {
            setFormData({
                name: testimonial.name || '',
                location: testimonial.location || '',
                rating: testimonial.rating || 5,
                text: testimonial.text || '',
                loan_type: testimonial.loan_type || '',
                display_order: testimonial.display_order || 0,
                is_active: testimonial.is_active ?? true
            });
            setPreviewUrl(testimonial.image_url || '');
        }
    }, [testimonial]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const data = new FormData();
        Object.keys(formData).forEach(key => {
            // Convert is_active to '1' or '0' for MySQL
            if (key === 'is_active') {
                data.append(key, formData[key] ? '1' : '0');
            } else {
                data.append(key, formData[key]);
            }
        });
        if (image) {
            data.append('image', image);
        }

        try {
            if (testimonial) {
                await axios.put(`/api/admin/testimonials/${testimonial.id}`, data);
                toast.success('Testimonial updated successfully');
            } else {
                await axios.post('/api/admin/testimonials', data);
                toast.success('Testimonial created successfully');
            }
            onSave();
        } catch (error) {
            console.error('Error saving testimonial:', error);
            toast.error(error.response?.data?.message || 'Failed to save testimonial');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800">
                            {testimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">
                            {testimonial ? 'Update customer feedback details' : 'Create a new customer success story'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white hover:shadow-md rounded-xl transition-all duration-200 text-slate-400 hover:text-slate-600"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Customer Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                                placeholder="e.g. Rajesh Kumar"
                            />
                        </div>

                        {/* Location */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Location</label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                                placeholder="e.g. Mumbai, Maharashtra"
                            />
                        </div>

                        {/* Loan Type */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Loan Type</label>
                            <input
                                type="text"
                                name="loan_type"
                                value={formData.loan_type}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                                placeholder="e.g. Home Construction Loan"
                            />
                        </div>

                        {/* Rating */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Rating (1-5)</label>
                            <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                                        className="focus:outline-none transition-transform hover:scale-110"
                                    >
                                        {star <= formData.rating ? (
                                            <StarIconSolid className="h-6 w-6 text-yellow-400" />
                                        ) : (
                                            <StarIcon className="h-6 w-6 text-slate-300" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Testimonial Text */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Testimonial Content</label>
                        <textarea
                            name="text"
                            value={formData.text}
                            onChange={handleInputChange}
                            required
                            rows={4}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none resize-none"
                            placeholder="Share the customer's experience..."
                        />
                    </div>

                    {/* Image Upload */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Customer Photo</label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-200 border-dashed rounded-2xl hover:border-primary-400 transition-colors bg-slate-50/50">
                            <div className="space-y-2 text-center">
                                {previewUrl ? (
                                    <div className="relative inline-block">
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            className="h-32 w-32 object-cover rounded-2xl shadow-lg border-2 border-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImage(null);
                                                setPreviewUrl('');
                                            }}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <PhotoIcon className="mx-auto h-12 w-12 text-slate-300" />
                                        <div className="flex text-sm text-slate-600">
                                            <label className="relative cursor-pointer bg-white rounded-md font-semibold text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                                                <span>Upload a file</span>
                                                <input
                                                    type="file"
                                                    className="sr-only"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />
                                            </label>
                                            <p className="pl-1">or drag and drop</p>
                                        </div>
                                        <p className="text-xs text-slate-500">PNG, JPG, WEBP up to 10MB</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Order & Status */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 rounded-2xl">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Display Order</label>
                            <input
                                type="number"
                                name="display_order"
                                value={formData.display_order}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-6">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleInputChange}
                                className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                            />
                            <label className="text-sm font-semibold text-slate-700">Active</label>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="px-8 py-6 border-t border-slate-100 flex items-center justify-end gap-4 bg-slate-50/50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 text-slate-600 font-semibold hover:bg-white hover:shadow-sm rounded-xl transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-8 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl shadow-lg shadow-primary-200 hover:shadow-primary-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Saving...' : 'Save Testimonial'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestimonialModal;
